import { useCallback, useMemo } from 'react';
import type { Vendor } from '../types';

interface UseVendorSelectionArgs {
  vendors: Vendor[];
  vendorUid: string | null;
  setVendorUid: (uid: string | null) => void;
}

export interface VendorSelection {
  /** The currently-selected vendor, or null if none / unknown. */
  vendor: Vendor | null;
  /**
   * All fanned-out records for the selected uid (one per booth).
   * Single-element array for normal vendors; empty when nothing is selected.
   */
  vendorBooths: Vendor[];
  /**
   * True when a uid is selected, vendors have loaded, and no vendor
   * matches — i.e. a dead deep link (unknown uid, or a vendor dropped
   * from this week's dataset via `here=no`). Always false while the
   * vendor list is still empty so a slow load isn't mistaken for a
   * missing vendor.
   */
  notFound: boolean;
  /** Open the detail overlay for a vendor. */
  open: (uid: string) => void;
  /** Close the detail overlay. */
  close: () => void;
}

/**
 * Bridges the URL-driven `vendorUid` (a string) and the in-memory
 * vendor list. Looks up the vendor via an O(1) map. Missing-uid keeps
 * the overlay closed and is surfaced via `notFound` once vendors have
 * loaded, so the caller can show feedback (dead deep link) and push
 * the URL clean.
 */
export function useVendorSelection({
  vendors,
  vendorUid,
  setVendorUid,
}: UseVendorSelectionArgs): VendorSelection {
  // O(1) lookup keyed by the stable Sheets-assigned uid. Deep links
  // survive row reorders because this key does not change across loads.
  // Multi-position vendors fan out into multiple records sharing the same
  // uid; keep the first one we encounter so the detail panel renders
  // deterministically (Positions-sheet order).
  const vendorByUid = useMemo(() => {
    const m = new Map<string, Vendor>();
    for (const v of vendors) if (!m.has(v.uid)) m.set(v.uid, v);
    return m;
  }, [vendors]);

  // All fanned-out records per uid — used for centroid pan + ping-all when
  // "Show on map" is triggered for a multi-position vendor.
  const vendorBoothsByUid = useMemo(() => {
    const m = new Map<string, Vendor[]>();
    for (const v of vendors) {
      const arr = m.get(v.uid) ?? [];
      arr.push(v);
      m.set(v.uid, arr);
    }
    return m;
  }, [vendors]);

  const vendor = vendorUid !== null ? (vendorByUid.get(vendorUid) ?? null) : null;
  const vendorBooths = vendorUid !== null ? (vendorBoothsByUid.get(vendorUid) ?? []) : [];
  const notFound = vendorUid !== null && vendors.length > 0 && vendor === null;

  const open = useCallback((uid: string) => setVendorUid(uid), [setVendorUid]);
  const close = useCallback(() => setVendorUid(null), [setVendorUid]);

  return { vendor, vendorBooths, notFound, open, close };
}
